import { useEffect } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { useRealtime } from '@/lib/realtime/core/useRealtime'
import { useFavoritesStore } from '@/store/favorites.store'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent } from '@scrambl/shared'

/**
 * App-level module freshness fallback.
 *
 * Every module below emits create/update/delete events to the
 * `organization:{orgId}` room, but the only listeners that patch their caches
 * are PAGE-SCOPED hooks (useProjectsRealtime, useDealsRealtime, etc.). When
 * those pages are closed nothing marks the module caches invalid, so a stale
 * list lingers until the next window-focus refetch.
 *
 * This hook subscribes to those org-room events at the application level and
 * marks the affected caches invalid with `refetchType: 'none'`:
 *   - Page-scoped hooks own surgical, in-place cache patching for mounted
 *     surfaces — we deliberately do NOT refetch them here.
 *   - `refetchType: 'none'` + the invalidation-aware global `refetchOnMount`
 *     (`query => query.state.isInvalidated`) handle closed pages: the marked
 *     query refetches when its page next mounts.
 *
 * Keep this dumb and uniform — no per-module cleverness beyond invalidating the
 * list key (plus the detail key when the payload carries an id).
 */
export function useGlobalModuleEventFallback() {
  const { socket, isReady } = useRealtime({ suppressLogging: true })
  const queryClient = useQueryClient()
  const removeDeletedFavorite = useFavoritesStore(
    state => state.removeDeletedFavorite,
  )

  useEffect(() => {
    if (!socket || !isReady) {
      return
    }

    const invalidate = (queryKey: readonly unknown[]) => {
      void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
    }

    // Every module payload here is a `Record<string, unknown>` carrying the
    // record's `id` (verified against each page-scoped hook). When present we
    // also mark the module's detail key so an open detail page refetches on its
    // next mount.
    const idOf = (data: unknown): string | undefined => {
      if (data && typeof data === 'object' && 'id' in data) {
        const id = (data as { id?: unknown }).id
        return typeof id === 'string' ? id : undefined
      }
      return undefined
    }

    const idsOf = (data: unknown): string[] => {
      const id = idOf(data)
      if (id) return [id]
      if (data && typeof data === 'object' && 'taskIds' in data) {
        const taskIds = (data as { taskIds?: unknown }).taskIds
        return Array.isArray(taskIds)
          ? taskIds.filter((item): item is string => typeof item === 'string')
          : []
      }
      return []
    }

    const favoriteDeleteEvents: Array<{
      event: SocketEvent
      types: string[]
    }> = [
      { event: SocketEvent.PROJECT_DELETED, types: ['PROJECT'] },
      { event: SocketEvent.TASK_DELETED, types: ['TASK'] },
      { event: SocketEvent.TASK_GROUP_DELETED, types: ['TASK_GROUP'] },
      { event: SocketEvent.TICKET_DELETED, types: ['TICKET'] },
      { event: SocketEvent.DEAL_DELETED, types: ['DEAL'] },
      { event: SocketEvent.CONTACT_DELETED, types: ['CONTACT'] },
      { event: SocketEvent.ENTITY_DELETED, types: ['ENTITY'] },
      { event: SocketEvent.EVENT_DELETED, types: ['EVENT'] },
      { event: SocketEvent.THREAD_DELETED, types: ['THREAD'] },
      {
        event: SocketEvent.THREAD_COMMENT_DELETED,
        types: ['THREAD_COMMENT'],
      },
      { event: SocketEvent.TIME_ENTRY_DELETED, types: ['TIME_ENTRY'] },
      { event: SocketEvent.NOTE_DELETED, types: ['NOTE'] },
      { event: SocketEvent.FORM_DELETED, types: ['FORM'] },
      {
        event: SocketEvent.COLLECTION_ENTRY_DELETED,
        types: ['CUSTOM_COLLECTION'],
      },
      { event: SocketEvent.MEMBER_DELETED, types: ['MEMBER'] },
      { event: SocketEvent.DOCUMENT_DELETED, types: ['DOCUMENT'] },
      { event: SocketEvent.WORKFLOW_DELETED, types: ['WORKFLOW'] },
      { event: SocketEvent.INVENTORY_ITEM_DELETED, types: ['INVENTORY'] },
      {
        event: SocketEvent.CYCLE_TEMPLATE_DELETED,
        types: ['CYCLE_TEMPLATE'],
      },
      {
        event: SocketEvent.CYCLE_INSTANCE_DELETED,
        types: ['CYCLE_INSTANCE'],
      },
    ]
    const favoriteDeleteHandlers = favoriteDeleteEvents.map(
      ({ event, types }) => {
        const handler = (data: unknown) => {
          for (const id of idsOf(data)) {
            for (const type of types) {
              void removeDeletedFavorite(id, type)
            }
          }
        }
        socket.on(event, handler)
        return { event, handler }
      },
    )

    // Projects — 'project:created/updated/deleted'
    const onProject = (data: Record<string, unknown>) => {
      invalidate(queryKeys.projects.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.projects.detail(id))
    }

    // Deals — 'deal:created/updated/deleted'
    const onDeal = (data: Record<string, unknown>) => {
      invalidate(queryKeys.deals.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.deals.detail(id))
    }

    // Contacts — 'contact:created/updated/deleted'
    const onContact = (data: Record<string, unknown>) => {
      invalidate(queryKeys.contacts.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.contacts.detail(id))
    }

    // Entities — 'entity:created/updated/deleted'
    const onEntity = (data: Record<string, unknown>) => {
      invalidate(queryKeys.entities.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.entities.detail(id))
    }

    // Forms — 'form:created/updated/deleted'
    const onForm = (data: Record<string, unknown>) => {
      invalidate(queryKeys.forms.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.forms.detail(id))
    }

    // Collections — 'collection:created/updated/deleted'
    const onCollectionCreated = (data: Record<string, unknown>) => {
      invalidate(queryKeys.customCollections.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.customCollections.detail(id))
    }
    const onCollectionUpdated = (data: Record<string, unknown>) => {
      invalidate(queryKeys.customCollections.lists())
      const id = idOf(data)
      if (id) {
        invalidate(queryKeys.customCollections.detail(id))
        // 'collectionEntry:*' events are emitted to per-collection rooms, NOT
        // the org room, so a global listener can never hear them. Piggyback the
        // entries invalidation on the parent SocketEvent.COLLECTION_UPDATED event so a
        // closed collection's entries also refetch on next mount.
        invalidate(queryKeys.customCollections.entries(id))
      }
    }
    const onCollectionDeleted = (data: Record<string, unknown>) => {
      invalidate(queryKeys.customCollections.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.customCollections.detail(id))
    }

    // Members — 'member:created/updated/deleted'
    const onMember = (data: Record<string, unknown>) => {
      invalidate(queryKeys.members.lists())
      invalidate(queryKeys.members.count())
      const id = idOf(data)
      if (id) invalidate(queryKeys.members.detail(id))
    }

    // Events — 'event:created/updated/deleted'
    const onEvent = (data: Record<string, unknown>) => {
      invalidate(queryKeys.events.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.events.detail(id))
    }

    // Documents — 'document:created/updated/deleted/acl-changed'
    const onDocument = (data: Record<string, unknown>) => {
      invalidate(queryKeys.documents.lists())
      invalidate(queryKeys.documents.counts())
      const id = idOf(data)
      if (id) invalidate(queryKeys.documents.detail(id))
    }

    // Workflows — WORKFLOW_CREATED/UPDATED/DELETED
    const onWorkflow = (data: Record<string, unknown>) => {
      invalidate(queryKeys.workflows.lists())
      const id = idOf(data)
      if (id) invalidate(queryKeys.workflows.detail(id))
    }

    // Workflow runs — WORKFLOW_RUN_CREATED/UPDATED. Payload carries `workflowId`;
    // the runs key is scoped under the parent workflow detail.
    const onWorkflowRun = (data: Record<string, unknown>) => {
      const workflowId =
        data &&
        typeof data === 'object' &&
        typeof (data as { workflowId?: unknown }).workflowId === 'string'
          ? (data as { workflowId: string }).workflowId
          : undefined
      if (workflowId) {
        invalidate(queryKeys.workflows.runs(workflowId))
      }
    }

    // Inventory — SocketEvent.INVENTORY_* (org room, verified in
    // apps/api/src/realtime/features/inventory-realtime.service.ts). The page
    // hook invalidates fine-grained keys; for the closed-page fallback marking
    // the whole namespace is sufficient — only what next mounts refetches.
    const onInventory = () => {
      invalidate(queryKeys.inventory.all)
    }
    const inventoryEvents = [
      SocketEvent.INVENTORY_ITEM_CREATED,
      SocketEvent.INVENTORY_ITEM_UPDATED,
      SocketEvent.INVENTORY_ITEM_DELETED,
      SocketEvent.INVENTORY_QUANTITY_CHANGED,
      SocketEvent.INVENTORY_MOVEMENT_RECORDED,
      SocketEvent.INVENTORY_ADJUSTMENT_CREATED,
      SocketEvent.INVENTORY_ADJUSTMENT_APPROVED,
      SocketEvent.INVENTORY_ADJUSTMENT_REJECTED,
      SocketEvent.INVENTORY_CATEGORY_CREATED,
      SocketEvent.INVENTORY_CATEGORY_UPDATED,
      SocketEvent.INVENTORY_CATEGORY_DELETED,
      SocketEvent.INVENTORY_LOCATION_CREATED,
      SocketEvent.INVENTORY_LOCATION_UPDATED,
      SocketEvent.INVENTORY_LOCATION_DELETED,
    ]

    // Planner — SocketEvent.PLANNER_* events, org room
    // (verified in apps/api/src/member-resources/member-resources-realtime.service.ts).
    // PLANNER_ALLOCATION_EDITING is presence-only on a per-allocation room — skipped.
    const onPlanner = () => {
      invalidate(queryKeys.planner.all)
    }
    const plannerEvents = [
      SocketEvent.PLANNER_ALLOCATION_CREATED,
      SocketEvent.PLANNER_ALLOCATION_UPDATED,
      SocketEvent.PLANNER_ALLOCATION_TRANSITIONED,
      SocketEvent.PLANNER_ALLOCATION_DELETED,
      SocketEvent.PLANNER_STAFFING_DECISION_CREATED,
    ]

    // Member resources — SocketEvent.MEMBER_RESOURCE_* events, org room (same service as planner).
    const onMemberResource = () => {
      invalidate(queryKeys.memberResources.all)
    }
    const memberResourceEvents = [
      SocketEvent.MEMBER_RESOURCE_RATE_CREATED,
      SocketEvent.MEMBER_RESOURCE_RATE_UPDATED,
      SocketEvent.MEMBER_RESOURCE_RATE_DELETED,
      SocketEvent.MEMBER_RESOURCE_ALLOCATION_CREATED,
      SocketEvent.MEMBER_RESOURCE_ALLOCATION_UPDATED,
      SocketEvent.MEMBER_RESOURCE_ALLOCATION_DELETED,
      SocketEvent.MEMBER_RESOURCE_ALLOCATION_CONFLICT,
      SocketEvent.MEMBER_RESOURCE_EARNINGS_CREATED,
      SocketEvent.MEMBER_RESOURCE_EARNINGS_UPDATED,
      SocketEvent.MEMBER_RESOURCE_EARNINGS_STATUS_CHANGED,
      SocketEvent.MEMBER_RESOURCE_SKILL_ADDED,
      SocketEvent.MEMBER_RESOURCE_SKILL_UPDATED,
      SocketEvent.MEMBER_RESOURCE_SKILL_REMOVED,
      SocketEvent.MEMBER_RESOURCE_UTILIZATION_UPDATED,
      SocketEvent.MEMBER_RESOURCE_UTILIZATION_CALCULATION_COMPLETE,
      SocketEvent.MEMBER_RESOURCE_PROFILE_CREATED,
      SocketEvent.MEMBER_RESOURCE_PROFILE_UPDATED,
    ]

    // Time-entry events are deliberately NOT handled here: the server emits
    // them to user rooms only, and every time-entry query uses the
    // always-fresh policy (staleTime: 0 + refetchOnMount: 'always'), so mounts
    // never serve stale data regardless of invalidation marks.

    socket.on(SocketEvent.PROJECT_CREATED, onProject)
    socket.on(SocketEvent.PROJECT_UPDATED, onProject)
    socket.on(SocketEvent.PROJECT_DELETED, onProject)

    socket.on(SocketEvent.DEAL_CREATED, onDeal)
    socket.on(SocketEvent.DEAL_UPDATED, onDeal)
    socket.on(SocketEvent.DEAL_DELETED, onDeal)

    socket.on(SocketEvent.CONTACT_CREATED, onContact)
    socket.on(SocketEvent.CONTACT_UPDATED, onContact)
    socket.on(SocketEvent.CONTACT_DELETED, onContact)

    socket.on(SocketEvent.ENTITY_CREATED, onEntity)
    socket.on(SocketEvent.ENTITY_UPDATED, onEntity)
    socket.on(SocketEvent.ENTITY_DELETED, onEntity)

    socket.on(SocketEvent.FORM_CREATED, onForm)
    socket.on(SocketEvent.FORM_UPDATED, onForm)
    socket.on(SocketEvent.FORM_DELETED, onForm)

    socket.on(SocketEvent.COLLECTION_CREATED, onCollectionCreated)
    socket.on(SocketEvent.COLLECTION_UPDATED, onCollectionUpdated)
    socket.on(SocketEvent.COLLECTION_DELETED, onCollectionDeleted)

    socket.on(SocketEvent.MEMBER_CREATED, onMember)
    socket.on(SocketEvent.MEMBER_UPDATED, onMember)
    socket.on(SocketEvent.MEMBER_DELETED, onMember)

    socket.on(SocketEvent.EVENT_CREATED, onEvent)
    socket.on(SocketEvent.EVENT_UPDATED, onEvent)
    socket.on(SocketEvent.EVENT_DELETED, onEvent)

    socket.on(SocketEvent.DOCUMENT_CREATED, onDocument)
    socket.on(SocketEvent.DOCUMENT_UPDATED, onDocument)
    socket.on(SocketEvent.DOCUMENT_DELETED, onDocument)
    socket.on(SocketEvent.DOCUMENT_ACL_CHANGED, onDocument)

    socket.on(SocketEvent.WORKFLOW_CREATED, onWorkflow)
    socket.on(SocketEvent.WORKFLOW_UPDATED, onWorkflow)
    socket.on(SocketEvent.WORKFLOW_DELETED, onWorkflow)
    socket.on(SocketEvent.WORKFLOW_RUN_CREATED, onWorkflowRun)
    socket.on(SocketEvent.WORKFLOW_RUN_UPDATED, onWorkflowRun)

    for (const event of inventoryEvents) socket.on(event, onInventory)
    for (const event of plannerEvents) socket.on(event, onPlanner)
    for (const event of memberResourceEvents) socket.on(event, onMemberResource)

    return () => {
      socket.off(SocketEvent.PROJECT_CREATED, onProject)
      socket.off(SocketEvent.PROJECT_UPDATED, onProject)
      socket.off(SocketEvent.PROJECT_DELETED, onProject)

      socket.off(SocketEvent.DEAL_CREATED, onDeal)
      socket.off(SocketEvent.DEAL_UPDATED, onDeal)
      socket.off(SocketEvent.DEAL_DELETED, onDeal)

      socket.off(SocketEvent.CONTACT_CREATED, onContact)
      socket.off(SocketEvent.CONTACT_UPDATED, onContact)
      socket.off(SocketEvent.CONTACT_DELETED, onContact)

      socket.off(SocketEvent.ENTITY_CREATED, onEntity)
      socket.off(SocketEvent.ENTITY_UPDATED, onEntity)
      socket.off(SocketEvent.ENTITY_DELETED, onEntity)

      socket.off(SocketEvent.FORM_CREATED, onForm)
      socket.off(SocketEvent.FORM_UPDATED, onForm)
      socket.off(SocketEvent.FORM_DELETED, onForm)

      socket.off(SocketEvent.COLLECTION_CREATED, onCollectionCreated)
      socket.off(SocketEvent.COLLECTION_UPDATED, onCollectionUpdated)
      socket.off(SocketEvent.COLLECTION_DELETED, onCollectionDeleted)

      socket.off(SocketEvent.MEMBER_CREATED, onMember)
      socket.off(SocketEvent.MEMBER_UPDATED, onMember)
      socket.off(SocketEvent.MEMBER_DELETED, onMember)

      socket.off(SocketEvent.EVENT_CREATED, onEvent)
      socket.off(SocketEvent.EVENT_UPDATED, onEvent)
      socket.off(SocketEvent.EVENT_DELETED, onEvent)

      socket.off(SocketEvent.DOCUMENT_CREATED, onDocument)
      socket.off(SocketEvent.DOCUMENT_UPDATED, onDocument)
      socket.off(SocketEvent.DOCUMENT_DELETED, onDocument)
      socket.off(SocketEvent.DOCUMENT_ACL_CHANGED, onDocument)

      socket.off(SocketEvent.WORKFLOW_CREATED, onWorkflow)
      socket.off(SocketEvent.WORKFLOW_UPDATED, onWorkflow)
      socket.off(SocketEvent.WORKFLOW_DELETED, onWorkflow)
      socket.off(SocketEvent.WORKFLOW_RUN_CREATED, onWorkflowRun)
      socket.off(SocketEvent.WORKFLOW_RUN_UPDATED, onWorkflowRun)

      for (const event of inventoryEvents) socket.off(event, onInventory)
      for (const event of plannerEvents) socket.off(event, onPlanner)
      for (const event of memberResourceEvents)
        socket.off(event, onMemberResource)
      for (const { event, handler } of favoriteDeleteHandlers)
        socket.off(event, handler)
    }
  }, [socket, isReady, queryClient, removeDeletedFavorite])
}
